import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { toast } from 'react-hot-toast';
import { useQuery, useQueryClient } from 'react-query';
import { Checkbox, Container, Grid, Header, Loader, Message, Pagination } from 'semantic-ui-react';
import { useUserContext } from '~/hooks';
import ReactGA from 'react-ga4';
import { orderBy } from 'lodash';
import { format, parseISO } from 'date-fns';

import { getCompanies } from '~/api/admin';
import { patchCompany, updateInvestorProfile } from '~/api/company';
import { useUpdateCompanyOwnership } from '~/mutations/admin/useUpdateCompanyOwnership';
import { useUpdateCompanySubtypes } from '~/mutations/admin/useUpdateCompanySubtypes';
import { CompanyOwnershipDisplay, CompanyOwnershipDropdownOptions } from '~/constants';
import { Button, Table } from '~/components';
import CompaniesFilters, { CompanyActiveFilter } from './CompaniesFilters';
import CreateCompanyModal from './CreateCompanyModal';
import { useAdminUsersQuery } from '~/requests/admin/useAdminUsersQuery';
import { CompanyOwnership, COMPANY_SUBTYPE_CODES, CompanySubtypeCode } from '~/types/company';

import '~/features/projectSearch/styles.scss';
import '~/features/bidSearch/styles.scss';
import './styles.scss';

const PAGE_SIZE = 50;
const COMPANY_NAME_FILTER_DEBOUNCE_MS = 400;

function formatLastActivityAt(iso: string): string {
  return format(parseISO(iso), 'MM/dd/yyyy h:mm a');
}

function truncateForMeta(s: string, max = 56): string {
  if (s.length <= max) {
    return s;
  }
  return `${s.slice(0, max - 1)}…`;
}

export default function AdminCompanies() {
  const { loggedInUser } = useUserContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>('lastActivityAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [companyNameFilter, setCompanyNameFilter] = useState('');
  const [debouncedCompanyName, setDebouncedCompanyName] = useState('');
  const [companyTypesFilter, setCompanyTypesFilter] = useState<string[]>([]);
  const [subtypeCodesFilter, setSubtypeCodesFilter] = useState<CompanySubtypeCode[]>([]);
  const [ownerIdsFilter, setOwnerIdsFilter] = useState<string[]>([]);
  const [thirdPartyExperiencesFilter, setThirdPartyExperiencesFilter] = useState<string[]>([]);
  const [companyActiveFilter, setCompanyActiveFilter] = useState<CompanyActiveFilter>('all');
  const [togglingCompanyId, setTogglingCompanyId] = useState<string | null>(null);
  const [togglingInvestorProfileId, setTogglingInvestorProfileId] = useState<string | null>(null);
  const [createCompanyModalOpen, setCreateCompanyModalOpen] = useState(false);

  useEffect(() => {
    const trimmed = companyNameFilter.trim();
    if (trimmed === '') {
      setDebouncedCompanyName('');
      return;
    }
    const id = window.setTimeout(() => {
      setDebouncedCompanyName(companyNameFilter);
    }, COMPANY_NAME_FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [companyNameFilter]);

  const companyNameSearch = debouncedCompanyName.trim();

  const companyActiveParam =
    companyActiveFilter === 'all'
      ? undefined
      : companyActiveFilter === 'active'
        ? 'true'
        : 'false';

  const filterSignature = useMemo(
    () =>
      [
        companyNameSearch,
        [...companyTypesFilter].sort().join(','),
        [...subtypeCodesFilter].sort().join(','),
        [...ownerIdsFilter].sort().join(','),
        [...thirdPartyExperiencesFilter].sort().join(','),
        companyActiveFilter,
      ].join('|'),
    [
      companyNameSearch,
      companyTypesFilter,
      subtypeCodesFilter,
      ownerIdsFilter,
      thirdPartyExperiencesFilter,
      companyActiveFilter,
    ]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterSignature]);

  const companyTypesParam =
    companyTypesFilter.length > 0 ? [...companyTypesFilter].sort().join(',') : undefined;
  const subtypeCodesParam =
    subtypeCodesFilter.length > 0 ? [...subtypeCodesFilter].sort().join(',') : undefined;
  const ownerIdsParam =
    ownerIdsFilter.length > 0 ? [...ownerIdsFilter].sort().join(',') : undefined;
  const thirdPartyExperiencesParam =
    thirdPartyExperiencesFilter.length > 0
      ? [...thirdPartyExperiencesFilter].sort().join(',')
      : undefined;
  const hasFilters =
    companyNameFilter.length > 0 ||
    companyTypesFilter.length > 0 ||
    subtypeCodesFilter.length > 0 ||
    ownerIdsFilter.length > 0 ||
    thirdPartyExperiencesFilter.length > 0 ||
    companyActiveFilter !== 'all';

  const clearAllFilters = () => {
    setCompanyNameFilter('');
    setCompanyTypesFilter([]);
    setSubtypeCodesFilter([]);
    setOwnerIdsFilter([]);
    setThirdPartyExperiencesFilter([]);
    setCompanyActiveFilter('all');
  };

  const queryKey = [
    'companies',
    currentPage,
    sortColumn,
    sortDirection,
    companyNameSearch,
    companyTypesParam ?? '',
    subtypeCodesParam ?? '',
    ownerIdsParam ?? '',
    thirdPartyExperiencesParam ?? '',
    companyActiveParam ?? '',
  ] as const;

  const { data, status: fetchCompaniesStatus } = useQuery(
    queryKey,
    () =>
      getCompanies({
        page: currentPage,
        limit: PAGE_SIZE,
        sortColumn: sortColumn === 'subtypeCodes' ? 'companyName' : sortColumn,
        sortDirection,
        companyName: companyNameSearch || undefined,
        companyTypes: companyTypesParam,
        subtypeCodes: subtypeCodesParam,
        ownerIds: ownerIdsParam,
        thirdPartyExperiences: thirdPartyExperiencesParam,
        companyActive: companyActiveParam,
      }),
    { refetchOnWindowFocus: false, keepPreviousData: true }
  );

  const { data: users, status: fetchUsersStatus } = useAdminUsersQuery();
  const { mutateAsync: updateCompanyOwnership, status: updateCompanyOwnershipStatus } = useUpdateCompanyOwnership();
  const { mutateAsync: updateCompanySubtypes, status: updateCompanySubtypesStatus } = useUpdateCompanySubtypes();

  const queryClient = useQueryClient();

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const tableRows = useMemo(() => {
    const list = data?.companies ?? [];
    if (sortColumn !== 'subtypeCodes') {
      return list;
    }
    return orderBy(
      list,
      [c => (c.subtypeCodes ?? []).slice().sort().join(',')],
      [sortDirection]
    );
  }, [data?.companies, sortColumn, sortDirection]);

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: '/project/id/review',
      title: 'Companies | Conductor Solar',
    });
    document.title = 'Companies | Conductor Solar';
  }, []);

  const getSort = (column: string) => {
    const sort = sortColumn === column ? sortDirection : undefined;
    if (sort === 'asc') return 'ascending';
    if (sort === 'desc') return 'descending';
    return sort;
  };

  const updateSort = (column: string) => {
    setCurrentPage(1);
    if (sortColumn === column) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleUpdate = async (
    id: string,
    key: string,
    value: string,
    row: { [key: string]: any },
    currentValue: string
  ) => {
    try {
      await updateCompanyOwnership({ id, key, value });
      toast.success('Successfully updated company data.');
    } catch (error) {
      console.error('Error updating company ownership:', error);
      toast.error('Failed to update company data. Please try again');
      row[key] = currentValue;
      queryClient.invalidateQueries(['companies']);
    }
  };

  const handleCompanyActiveToggle = async (company: CompanyOwnership, nextActive: boolean) => {
    const id = String(company.companyId);
    if (togglingCompanyId != null) {
      return;
    }
    const previousActive = company.companyActive;
    setTogglingCompanyId(id);
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old?.companies) return old;
      return {
        ...old,
        companies: old.companies.map((c: CompanyOwnership) =>
          String(c.companyId) === id ? { ...c, companyActive: nextActive } : c
        ),
      };
    });
    try {
      await patchCompany(id, { active: nextActive });
      toast.success(`Company ${nextActive ? 'activated' : 'deactivated'}.`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update company status.');
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.companies) return old;
        return {
          ...old,
          companies: old.companies.map((c: CompanyOwnership) =>
            String(c.companyId) === id ? { ...c, companyActive: previousActive } : c
          ),
        };
      });
    } finally {
      setTogglingCompanyId(null);
    }
  };

  const handleInvestorProfileToggle = async (
    company: CompanyOwnership,
    field: 'investorIsVisible' | 'investorFullAccess',
    nextValue: boolean
  ) => {
    const profileId = company.investorProfileId;
    if (!profileId) {
      return;
    }
    if (togglingInvestorProfileId != null) {
      return;
    }
    const previousValue =
      field === 'investorIsVisible' ? company.investorIsVisible : company.investorFullAccess;
    const payloadKey = field === 'investorIsVisible' ? 'isVisible' : 'fullAccess';
    setTogglingInvestorProfileId(profileId);
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old?.companies) return old;
      return {
        ...old,
        companies: old.companies.map((c: CompanyOwnership) =>
          c.investorProfileId === profileId ? { ...c, [field]: nextValue } : c
        ),
      };
    });
    try {
      await updateInvestorProfile(profileId, { [payloadKey]: nextValue });
      toast.success('Successfully updated investor profile.');
    } catch (error) {
      console.error('Error updating investor profile:', error);
      toast.error('Failed to update investor profile. Please try again.');
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.companies) return old;
        return {
          ...old,
          companies: old.companies.map((c: CompanyOwnership) =>
            c.investorProfileId === profileId ? { ...c, [field]: previousValue } : c
          ),
        };
      });
    } finally {
      setTogglingInvestorProfileId(null);
    }
  };

  const handleSubtypeCodesChange = async (company: CompanyOwnership, newSubtypeCodes: CompanySubtypeCode[]) => {
    const previousSubtypeCodes = company.subtypeCodes ?? [];
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old?.companies) return old;
      return {
        ...old,
        companies: old.companies.map((c: CompanyOwnership) =>
          c.companyId === company.companyId ? { ...c, subtypeCodes: newSubtypeCodes } : c
        ),
      };
    });
    try {
      await updateCompanySubtypes({ id: company.companyId, subtypeCodes: newSubtypeCodes });
      toast.success('Successfully updated company tags.');
    } catch (error) {
      console.error('Error updating company subtypes:', error);
      toast.error('Failed to update company tags. Please try again');
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.companies) return old;
        return {
          ...old,
          companies: old.companies.map((c: CompanyOwnership) =>
            c.companyId === company.companyId ? { ...c, subtypeCodes: previousSubtypeCodes } : c
          ),
        };
      });
    }
  };

  const updateCompanyValue = (id: string, key: string, value: string) => {
    const companies = data?.companies ?? [];
    const row = companies.find((company: CompanyOwnership) => company.companyId === id);
    if (!row) {
      return;
    }

    const mutableRow = row as CompanyOwnership & Record<string, string | undefined>;
    const currentValue = mutableRow[key];
    mutableRow[key] = value;
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old) return old;
      return { ...old, companies: [...old.companies] };
    });

    handleUpdate(id, key, value, mutableRow, currentValue as string);
  };

  const isLoading =
    fetchCompaniesStatus === 'loading' ||
    fetchUsersStatus === 'loading' ||
    updateCompanyOwnershipStatus === 'loading' ||
    updateCompanySubtypesStatus === 'loading';

  const companiesOnPage = tableRows.length;
  const totalCount = data?.total ?? 0;
  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd =
    totalCount === 0
      ? 0
      : Math.min((currentPage - 1) * PAGE_SIZE + companiesOnPage, totalCount);

  const showInitialCompaniesLoader = fetchCompaniesStatus === 'loading' && !data;
  const showCompaniesEmpty = fetchCompaniesStatus === 'success' && totalCount === 0;
  const showCompaniesTable = data != null && totalCount > 0;

  const activeFilterDescription = useMemo(() => {
    const parts: string[] = [];
    if (companyNameSearch) {
      parts.push(`Name contains “${truncateForMeta(companyNameSearch)}”`);
    }
    if (companyTypesFilter.length > 0) {
      parts.push(`Type: ${[...companyTypesFilter].sort().join(', ')}`);
    }
    if (subtypeCodesFilter.length > 0) {
      parts.push(`Subtypes: ${[...subtypeCodesFilter].sort().join(', ')}`);
    }
    if (ownerIdsFilter.length > 0) {
      const names = [...ownerIdsFilter]
        .sort()
        .map(id => users?.find(u => u.id === id)?.name ?? id);
      parts.push(`Lead: ${names.join(', ')}`);
    }
    if (thirdPartyExperiencesFilter.length > 0) {
      parts.push(`Tier: ${[...thirdPartyExperiencesFilter].sort().join(', ')}`);
    }
    if (companyActiveFilter === 'active') {
      parts.push('Status: Active');
    } else if (companyActiveFilter === 'deactivated') {
      parts.push('Status: Deactivated');
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [
    companyNameSearch,
    companyTypesFilter,
    subtypeCodesFilter,
    ownerIdsFilter,
    thirdPartyExperiencesFilter,
    companyActiveFilter,
    users,
  ]);

  if (!loggedInUser?.isAdmin) {
    return <Message negative>Only admin users can view the companies list.</Message>;
  }

  return (
    <div className='project-search-container bid-search-container'>
      <Container fluid>
        <Grid centered>
          <Grid.Column mobile={12} tablet={12} computer={12} largeScreen={12}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.75rem',
                marginBottom: '0.5rem',
              }}
            >
              <Header as='h1' textAlign='left' style={{ marginBottom: 0 }}>
                Companies
              </Header>
              <Button primary onClick={() => setCreateCompanyModalOpen(true)}>
                New company
              </Button>
            </div>
            {createCompanyModalOpen && (
              <CreateCompanyModal
                closeModal={() => setCreateCompanyModalOpen(false)}
                adminUsers={users?.map(u => ({ id: u.id, name: u.name })) ?? []}
              />
            )}
            <div className='card'>
              <CompaniesFilters
                companyName={companyNameFilter}
                onCompanyNameChange={setCompanyNameFilter}
                companyTypes={companyTypesFilter}
                onCompanyTypesChange={setCompanyTypesFilter}
                subtypeCodes={subtypeCodesFilter}
                onSubtypeCodesChange={setSubtypeCodesFilter}
                ownerIds={ownerIdsFilter}
                onOwnerIdsChange={setOwnerIdsFilter}
                thirdPartyExperiences={thirdPartyExperiencesFilter}
                onThirdPartyExperiencesChange={setThirdPartyExperiencesFilter}
                companyActiveFilter={companyActiveFilter}
                onCompanyActiveFilterChange={setCompanyActiveFilter}
                adminUsers={users?.map(u => ({ id: u.id, name: u.name })) ?? []}
              />
            </div>

            <div className='card'>
              {showInitialCompaniesLoader && <Loader active inline='centered' />}
              {showCompaniesEmpty && (
                <Message info>No companies found matching your filters.</Message>
              )}
              {showCompaniesTable && (
                <>
                  {isLoading && <Table.Loader />}
                  <div
                    style={
                      isLoading
                        ? {
                            marginTop: 0,
                            minHeight: 'calc(100vh - 16rem)',
                            background: 'var(--color-pure-white)',
                          }
                        : {}
                    }
                  >
                    <div className='admin-companies-search-meta'>
                      Showing {rangeStart} - {rangeEnd} of {totalCount}{' '}
                      {totalCount === 1 ? 'company' : 'companies'}
                      {hasFilters && (
                        <>
                          {' - '}
                          <button
                            type='button'
                            className='admin-companies-clear-link'
                            onClick={clearAllFilters}
                          >
                            Clear filters
                          </button>
                        </>
                      )}
                      {activeFilterDescription && (
                        <>
                          <br />
                          {activeFilterDescription}
                        </>
                      )}
                    </div>
                    <Table className='admin-companies-table' striped textAlign='left' sortable>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell
                            textAlign='left'
                            sorted={getSort('companyName')}
                            onClick={() => updateSort('companyName')}
                          >
                            Company Name
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            textAlign='left'
                            sorted={getSort('companyType')}
                            onClick={() => updateSort('companyType')}
                          >
                            Company type
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            textAlign='left'
                            sorted={getSort('subtypeCodes')}
                            onClick={() => updateSort('subtypeCodes')}
                          >
                            Subtypes
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            textAlign='left'
                            sorted={getSort('ownerName')}
                            onClick={() => updateSort('ownerName')}
                          >
                            Conductor Lead
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            textAlign='left'
                            sorted={getSort('thirdPartyExperience')}
                            onClick={() => updateSort('thirdPartyExperience')}
                          >
                            {CompanyOwnershipDisplay.THIRD_PARTY_EXPERIENCE}
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            textAlign='left'
                            sorted={getSort('lastActivityAt')}
                            onClick={() => updateSort('lastActivityAt')}
                          >
                            Last activity
                          </Table.HeaderCell>
                          <Table.HeaderCell textAlign='center' style={{ textAlign: 'center' }}>Profile visibility</Table.HeaderCell>
                          <Table.HeaderCell textAlign='center' style={{ textAlign: 'center' }}>Listings access</Table.HeaderCell>
                          <Table.HeaderCell textAlign='center' style={{ textAlign: 'center' }}>Active</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {tableRows.map((company: CompanyOwnership) => (
                          <Table.Row key={company.companyId}>
                            <Table.Cell>
                              <span className='admin-companies-name-cell'>
                                <a href={`/company-profile/${company.companyId}`}>{company.companyName}</a>
                                {company.companyActive === false && (
                                  <span className='admin-companies-deactivated-badge'>Deactivated</span>
                                )}
                              </span>
                            </Table.Cell>
                            <Table.Cell>{company.companyType ?? '-'}</Table.Cell>
                            <Table.Cell
                              isEditable
                              multiple
                              style={{ minWidth: '10rem' }}
                              value={company.subtypeCodes ?? []}
                              defaultValue='-'
                              options={COMPANY_SUBTYPE_CODES.map(code => ({
                                key: code,
                                text: code,
                                value: code,
                              }))}
                              onChange={value =>
                                handleSubtypeCodesChange(company, value as CompanySubtypeCode[])
                              }
                            />
                            <Table.Cell
                              isEditable
                              value={company.ownerId}
                              defaultValue='-'
                              options={
                                users?.map(user => ({
                                  key: user.id,
                                  text: user.name,
                                  value: user.id,
                                })) ?? []
                              }
                              onChange={value => {
                                updateCompanyValue(company.companyId, 'ownerId', value);
                                queryClient.invalidateQueries('ownership');
                              }}
                            />
                            <Table.Cell
                              isEditable
                              value={company.thirdPartyExperience}
                              defaultValue='-'
                              options={CompanyOwnershipDropdownOptions}
                              onChange={value => updateCompanyValue(company.companyId, 'thirdPartyExperience', value)}
                            />
                            <Table.Cell>
                              {company.lastActivityAt ? formatLastActivityAt(company.lastActivityAt) : '—'}
                            </Table.Cell>
                            <Table.Cell
                              textAlign='center'
                              className='admin-companies-active-cell'
                              onClick={(e: SyntheticEvent) => e.stopPropagation()}
                            >
                              {company.investorProfileId ? (
                                <Checkbox
                                  toggle
                                  checked={company.investorIsVisible === true}
                                  disabled={togglingInvestorProfileId != null}
                                  onChange={(_, data) => {
                                    if (typeof data.checked === 'boolean') {
                                      void handleInvestorProfileToggle(
                                        company,
                                        'investorIsVisible',
                                        data.checked
                                      );
                                    }
                                  }}
                                />
                              ) : (
                                '—'
                              )}
                            </Table.Cell>
                            <Table.Cell
                              textAlign='center'
                              className='admin-companies-active-cell'
                              onClick={(e: SyntheticEvent) => e.stopPropagation()}
                            >
                              {company.investorProfileId ? (
                                <Checkbox
                                  toggle
                                  checked={company.investorFullAccess === true}
                                  disabled={togglingInvestorProfileId != null}
                                  onChange={(_, data) => {
                                    if (typeof data.checked === 'boolean') {
                                      void handleInvestorProfileToggle(
                                        company,
                                        'investorFullAccess',
                                        data.checked
                                      );
                                    }
                                  }}
                                />
                              ) : (
                                '—'
                              )}
                            </Table.Cell>
                            <Table.Cell
                              textAlign='center'
                              className='admin-companies-active-cell'
                              onClick={(e: SyntheticEvent) => e.stopPropagation()}
                            >
                              <Checkbox
                                toggle
                                checked={company.companyActive !== false}
                                disabled={togglingCompanyId != null}
                                onChange={(_, data) => {
                                  if (typeof data.checked === 'boolean') {
                                    void handleCompanyActiveToggle(company, data.checked);
                                  }
                                }}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                    {totalPages > 1 && (
                      <div
                        style={{
                          marginTop: '2rem',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Pagination
                          activePage={currentPage}
                          onPageChange={(_, { activePage }) => setCurrentPage(activePage as number)}
                          totalPages={totalPages}
                          siblingRange={1}
                          boundaryRange={1}
                          ellipsisItem={null}
                          firstItem={null}
                          lastItem={null}
                          prevItem={{ content: '←', icon: false }}
                          nextItem={{ content: '→', icon: false }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Grid.Column>
        </Grid>
      </Container>
    </div>
  );
}
